<template>
    <Paper class="paper">
        <h2 class="headline  py-3 px-4 m-0">
            {{ $t('spotlight.education.dialog.addNewEducation') }}
        </h2>
        <Divider />
        <div class="content px-4 py-4">
            <TextField
                :label="$t('spotlight.education.dialog.degreeTitle')"
                v-model="degreeName"
                :placeholder="$t('spotlight.education.dialog.degreeTitle')"
                margin
                :helperText="degreeError"
                :error="degreeError !== null"
            />

            <h6 class="subtitle bold">
                {{ $t('spotlight.education.dialog.institution') }}
            </h6>
            <BaseSearchable
                v-model="school"
                :data="schoolData()"
                class="mb-4"
                creatable
                :placeholder="$t('spotlight.education.dialog.institution')"
                :helperText="schoolError"
                :error="schoolError !== null"
            />

            <div class="degree-info">
                <div class="w-100">
                    <h6 class="subtitle bold">
                        {{ $t('spotlight.education.dialog.degreeType') }}
                    </h6>
                    <BaseSearchable
                        v-model="level"
                        :data="levels()"
                        class="mb-4"
                        :placeholder="
                            $t('spotlight.education.dialog.degreeType')
                        "
                        :helperText="levelError"
                        :error="levelError !== null"
                    />
                </div>
                <div class="w-100">
                    <h6 class="subtitle bold">
                        {{ $t('spotlight.education.dialog.fieldOfStudy') }}
                    </h6>
                    <BaseSearchable
                        v-model="field"
                        :data="fields()"
                        class="mb-4"
                        :placeholder="
                            $t('spotlight.education.dialog.fieldOfStudy')
                        "
                        :helperText="fieldError"
                        :error="fieldError !== null"
                    />
                </div>
            </div>

            <div class="date-content">
                <div>
                    <h6 class="subtitle bold">
                        {{ $t('spotlight.education.dialog.studyStart') }}
                    </h6>
                    <YearMonthPicker
                        v-model="start"
                        :defaultText="
                            $t('spotlight.education.dialog.selectStartDate')
                        "
                    />
                </div>
                <div>
                    <h6 class="subtitle bold">
                        {{ $t('spotlight.education.dialog.studyEnd') }}
                    </h6>
                    <YearMonthPicker
                        v-model="end"
                        :defaultText="
                            $t('spotlight.education.dialog.selectEndDate')
                        "
                    />
                </div>
            </div>
            <div v-for="(error, i) in [startError, endError]" :key="i">
                <h6 v-if="error" class="subtitle mt-2 error-color">
                    {{ error }}
                </h6>
            </div>

            <div class="d-flex flex-row-reverse mt-3">
                <BaseButton variant="secondary" @click="handleAction">
                    {{ degree ? $t('general.save') : $t('general.add') }}
                </BaseButton>
                <BaseButton
                    class="mr-3"
                    variant="text-secondary"
                    @click="onClose"
                    >{{ $t('general.close') }}</BaseButton
                >
                <div class="flex-grow" />
                <BaseButton
                    v-if="degree"
                    variant="text-error"
                    @click="onDelete"
                    >{{ $t('general.delete') }}</BaseButton
                >
            </div>
        </div>
    </Paper>
</template>

<script>
// Utils
import degreeFields from 'highered-utils/data/degreeFields.json';
import degreeLevels from 'highered-utils/data/degreeLevels.json';

export default {
    name: 'EducationModal',
    props: {
        degree: {
            // An existing degree
            type: Object,
            default: null,
        },
    },
    data: () => ({
        degreeName: '',
        school: null,
        start: null,
        end: null,
        field: null,
        level: null,
        degreeError: null,
        schoolError: null,
        startError: null,
        endError: null,
        fieldError: null,
        levelError: null,
    }),
    computed: {
        schools() {
            return this.$interfaces.candidate.school.get({ reactive: true });
        },
    },
    methods: {
        schoolData() {
            return this.schools.data.map(s => ({ value: s.id, label: s.name }));
        },
        fields() {
            return degreeFields.map(f => {
                const langFieldKey = `spotlight.education.dialog.diciplins.${
                    f.value
                }`; // i18n key
                return {
                    ...f,
                    label: this.$te(langFieldKey)
                        ? this.$t(langFieldKey)
                        : f.label,
                };
            });
        },
        levels() {
            return degreeLevels.map(l => {
                const langDegreeKey = `spotlight.education.dialog.degrees.${
                    l.value
                }`; // i18n key
                return {
                    ...l,
                    label: this.$te(langDegreeKey)
                        ? this.$t(langDegreeKey)
                        : l.label,
                };
            });
        },
        onClose() {
            this.clear();
            this.$emit('close');
            this.$interfaces.dialogModal.getState().resolver.res();
        },
        clear() {
            this.degreeName = '';
            this.start = null;
            this.end = null;
            this.school = null;
            this.field = null;
            this.level = null;
            this.degreeError = null;
            this.schoolError = null;
            this.startError = null;
            this.endError = null;
            this.fieldError = null;
            this.levelError = null;
        },
        getInputData() {
            return {
                name: this.degreeName,
                school: this.school,
                start: this.start.toISOString(),
                end: this.end.toISOString(),
                school_id: (this.school || {}).value,
                field: { id: this.field.value, name: this.field.label },
                level: { id: this.level.value, name: this.level.label },
                field_id: this.field.value,
                level_id: this.level.value,
            };
        },
        onAdd() {
            if (!this.isValidInput()) {
                return;
            }

            const data = this.getInputData();

            this.$emit('create', data);
            this.onClose();
        },
        onSave() {
            if (!this.isValidInput()) {
                return;
            }

            const data = { ...this.degree, ...this.getInputData() };

            this.$emit('save', data);
            this.onClose();
        },
        onDelete() {
            this.$emit('delete', this.degree.id);
            this.onClose();
        },
        handleAction() {
            if (this.degree) {
                this.onSave();
            } else {
                this.onAdd();
            }
        },
        isValidInput() {
            // Validate input data
            if (!this.degreeName) {
                this.degreeError = this.$t(
                    'spotlight.education.dialog.invalidDegreeTitle'
                );
                return false;
            } else if (!this.school) {
                this.schoolError = this.$t(
                    'spotlight.education.dialog.invalidSchool'
                );
                return false;
            } else if (!this.level) {
                this.levelError = this.$t(
                    'spotlight.education.dialog.invalidDegreeLevel'
                );
                return false;
            } else if (!this.field) {
                this.fieldError = this.$t(
                    'spotlight.education.dialog.invalidDegreeField'
                );
                return false;
            } else if (!this.start) {
                this.startError = this.$t(
                    'spotlight.education.dialog.invalidStartDate'
                );
                return false;
            } else if (!this.end) {
                this.startError = this.$t(
                    'spotlight.education.dialog.invalidEndDate'
                );
                return false;
            } else if (this.end < this.start) {
                this.startError = this.$t(
                    'spotlight.education.dialog.invalidStudyPeriod'
                );
                return false;
            }
            return true;
        },
        mapPropsToData(newVal) {
            // newVal is a degree-object
            if (!newVal || !newVal.school) {
                return;
            }

            this.degreeName = newVal.name;
            this.school = {
                ...newVal.school,
                value: newVal.school.id,
                label: newVal.school.name,
            };

            const langFieldKey = `spotlight.education.dialog.diciplins.${
                newVal.field_id
            }`;
            const langLevelKey = `spotlight.education.dialog.degrees.${
                newVal.level_id
            }`;
            this.field = {
                value: newVal.field_id,
                label: this.$te(langFieldKey)
                    ? this.$t(langFieldKey)
                    : newVal.field.name,
            };
            this.level = {
                value: newVal.level_id,
                label: this.$te(langLevelKey)
                    ? this.$t(langLevelKey)
                    : newVal.field.name,
            };
            this.end = new Date(newVal.end);
            this.start = new Date(newVal.start);
        },
    },
    mounted() {
        this.mapPropsToData(this.degree);
    },
    watch: {
        // Initializes values on degree props changed
        degree(newVal) {
            this.mapPropsToData(newVal);
        },
        degreeName() {
            this.degreeError = null;
        },
        school() {
            this.schoolError = null;
        },
        start() {
            this.startError = null;
        },
        end() {
            this.endError = null;
        },
        field() {
            this.fieldError = null;
        },
        level() {
            this.levelError = null;
        },
    },
};
</script>

<style scoped>
.paper {
    width: 96%;
    max-width: 600px;
}
.date-content {
    display: grid;
    grid-template-columns: 1fr 1fr;

    grid-gap: 16px;
}

.degree-info {
    display: grid;
    grid-template-columns: 1fr 1fr;

    grid-gap: 16px;
}

@media only screen and (max-width: 600px) {
    .degree-info {
        grid-template-columns: 1fr;
        grid-template-rows: 1fr 1fr;
        grid-gap: 0px;
    }

    .paper {
        width: 100%;
        padding-bottom: 100px;
        border-radius: 0 !important;
    }

    .date-content {
        grid-template-columns: 1fr;
    }
}
</style>
